import type { ResultEnvelope } from "../types";

// API 베이스: 동일 출처가 아닐 경우 (예: localhost:5173 → localhost:3001) 환경변수 사용 가능
const API_BASE = import.meta.env.VITE_API_BASE || "";

const _KF_POLL_COMPONENT = "ai_top_picks";
const _KF_POLL_INTERVAL_MS = 500;
const _KF_POLL_MAX_ATTEMPTS = 6;

function itemsLackKeyFeatures(env: ResultEnvelope): boolean {
  const items = (env.data as { items?: Array<{ keyFeatures?: unknown[] }> })?.items;
  if (!items?.length) return false;
  return items.some((it) => !it.keyFeatures?.length);
}

/** recommend fast path(`products.py` `_RUNTIME_METHOD`)만 BG contextual_features spawn. */
function isDeferredKeyFeaturesPath(env: ResultEnvelope): boolean {
  const meta = (env.data as { metadata?: { method?: string } })?.metadata;
  return meta?.method === "nl_to_dsl";
}

// 위젯 → agent /api/widget/* 프록시는 인증키를 요구하지 않는다.
// 결과는 고유 resultId + Mcp-Session-Id 바인딩으로 보호되며(타 세션 접근 불가),
// agent 가 mcp 호출 시 서버측 MCP_API_KEY 로 인증한다(브라우저 키 불필요).
export async function fetchResult(
  resultId: string,
  sessionToken: string
): Promise<ResultEnvelope> {
  const headers: Record<string, string> = {
    "Mcp-Session-Id": sessionToken,
  };
  const res = await fetch(`${API_BASE}/api/widget/result/${resultId}`, {
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`failed to fetch result: ${res.status}`);
  }
  return (await res.json()) as ResultEnvelope;
}

/**
 * ai_top_picks — contextual_features BG cache update 레이스 완화.
 * 1차 fetch 후 비블로킹으로 재조회; 카드는 먼저 표시하고 KF만 늦게 반영.
 */
export async function pollKeyFeaturesIfNeeded(
  resultId: string,
  sessionToken: string,
  env: ResultEnvelope,
  onUpdate: (env: ResultEnvelope) => void,
  isCancelled?: () => boolean,
): Promise<void> {
  if (
    env.component !== _KF_POLL_COMPONENT
    || !isDeferredKeyFeaturesPath(env)
    || !itemsLackKeyFeatures(env)
  ) {
    return;
  }

  for (let attempt = 0; attempt < _KF_POLL_MAX_ATTEMPTS; attempt++) {
    if (isCancelled?.()) return;
    await new Promise((resolve) => setTimeout(resolve, _KF_POLL_INTERVAL_MS));
    if (isCancelled?.()) return;
    try {
      const next = await fetchResult(resultId, sessionToken);
      if (isCancelled?.()) return;
      if (!itemsLackKeyFeatures(next)) {
        onUpdate(next);
        return;
      }
    } catch {
      return;
    }
  }
}

export async function callTool(
  name: string,
  args: Record<string, unknown>,
  sessionToken: string,
): Promise<unknown> {
  const res = await fetch(`${API_BASE}/api/widget/tool-call`, {
    method: "POST",
    headers: {
      "Mcp-Session-Id": sessionToken,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name, arguments: args }),
    credentials: "include",
  });
  if (!res.ok) throw new Error(`tool-call failed: ${res.status}`);
  return await res.json();
}
